import React, { useEffect, useState } from 'react';
import { X, Trash2, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { deleteEvent, EVENT_TYPE_LABELS } from '../services/api';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';

const fmtTimestamp = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Modal de confirmación + ejecución del borrado masivo de fichajes.
// Props:
//   open       — boolean, controla visibilidad.
//   events     — array de eventos a borrar (objetos { id, eventType, timestamp, workerId, ... }).
//   workerName — string opcional para mostrar el dueño cuando el listado es de un único trabajador.
//   workerLookup — opcional Map/Object id→name para el caso multi-trabajador (informes con scope amplio).
//   onClose    — callback cierre sin acción.
//   onDeleted  — callback (deletedIds[], errors[]) tras terminar; el padre refresca.
const DeleteEventsConfirmModal = ({ open, events = [], workerName, workerLookup, onClose, onDeleted }) => {
  const [phase, setPhase] = useState('confirm'); // confirm | running | done
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const toast = useToast();

  useEffect(() => {
    if (open) {
      setPhase('confirm');
      setProgress(0);
      setErrors([]);
    }
  }, [open]);

  if (!open) return null;
  const count = events.length;

  const handleConfirm = async () => {
    setPhase('running');
    const deletedIds = [];
    const errs = [];
    for (let i = 0; i < events.length; i++) {
      const ev = events[i];
      try {
        await deleteEvent(ev.id);
        deletedIds.push(ev.id);
      } catch (err) {
        errs.push({ id: ev.id, message: err?.message || 'Error desconocido' });
      }
      setProgress(i + 1);
    }
    setErrors(errs);
    setPhase('done');
    // Un único resumen al final (nunca un toast por fichaje).
    const summary = FEEDBACK.eventsDeleted(deletedIds.length, errs.length);
    toast.show(summary.type, summary.message);
    onDeleted?.(deletedIds, errs);
  };

  const lookupName = (workerId) => {
    if (!workerLookup) return null;
    if (typeof workerLookup.get === 'function') return workerLookup.get(workerId);
    return workerLookup[workerId] || null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-red-600 font-semibold">Eliminar fichajes</div>
            <h2 className="font-display text-lg font-bold text-ink">
              {count === 1 ? '¿Eliminar este fichaje?' : `¿Eliminar ${count} fichajes?`}
            </h2>
            {workerName && (
              <div className="text-xs text-ink-muted mt-0.5 truncate">De {workerName}</div>
            )}
          </div>
          {phase !== 'running' && (
            <button onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-md transition" aria-label="Cerrar">
              <X size={20} />
            </button>
          )}
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {phase === 'confirm' && (
            <>
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  Esta acción es <strong>irreversible</strong>. Los fichajes se borrarán de la base de datos definitivamente.
                </div>
              </div>
              <div className="space-y-2">
                {events.slice(0, 50).map((ev) => {
                  const name = lookupName(ev.workerId);
                  return (
                    <div key={ev.id} className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-sm">
                      <Trash2 size={14} className="text-red-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-ink truncate">
                          {EVENT_TYPE_LABELS[ev.eventType] || `Tipo ${ev.eventType}`}
                          {name && <span className="text-ink-muted font-normal"> · {name}</span>}
                        </div>
                        <div className="text-xs text-ink-muted">{fmtTimestamp(ev.timestamp)}</div>
                      </div>
                    </div>
                  );
                })}
                {events.length > 50 && (
                  <div className="text-xs text-ink-muted text-center pt-2">
                    Y {events.length - 50} fichaje{events.length - 50 === 1 ? '' : 's'} más…
                  </div>
                )}
              </div>
            </>
          )}

          {phase === 'running' && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 size={32} className="animate-spin text-red-500" />
              <div className="text-sm font-medium text-ink">Eliminando…</div>
              <div className="text-xs text-ink-muted">{progress} / {count}</div>
              <div className="w-full bg-slate-100 rounded-full h-2 max-w-xs">
                <div
                  className="bg-red-500 h-2 rounded-full transition-all"
                  style={{ width: `${count ? (progress / count) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div className="flex flex-col items-center justify-center py-6 gap-3">
              {errors.length === 0 ? (
                <>
                  <CheckCircle2 size={36} className="text-emerald-500" />
                  <div className="text-base font-semibold text-ink">
                    {count === 1 ? 'Fichaje eliminado' : `${count} fichajes eliminados`}
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle size={36} className="text-amber-500" />
                  <div className="text-base font-semibold text-ink">
                    {count - errors.length} eliminado{count - errors.length === 1 ? '' : 's'}, {errors.length} con error
                  </div>
                  <ul className="text-xs text-red-600 space-y-1 max-h-40 overflow-y-auto w-full">
                    {errors.map((e, i) => (
                      <li key={i}>· id {e.id}: {e.message}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          {phase === 'confirm' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink rounded-lg transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition flex items-center gap-2"
              >
                <Trash2 size={14} /> Eliminar {count > 1 ? `${count} fichajes` : 'fichaje'}
              </button>
            </>
          )}
          {phase === 'done' && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-white bg-ink hover:bg-ink/90 rounded-lg transition"
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteEventsConfirmModal;
